/**
 * PORTED — RO-11-03 / #44588.
 * Origin: `excellent/app/_chrome/mode-rail.tsx`.
 *
 * Changes: next/link + next/navigation → @excellent/ui/router (RouterLink,
 * usePathname, useSearch). Copied into client/shell/chrome/ — never import
 * from app/ (PRD D6).
 */

import { RouterLink as Link, usePathname, useSearch } from "@excellent/ui/router";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
  cn,
  Icon,
  pickActiveTarget,
  useModeSubNav,
  NAV_FROM_PARAM,
  type ModeSubNavItem,
} from "@excellent/ui";
import { MODES, modePath } from "@/modes/modes-registry";
import { isModeActive } from "@/modes/mode-active";
import { projectSlugFromPath } from "@/modes/project-path";
import {
  useModeWall,
  useNavTelemetry,
  NavSparkline,
  NavSparklineDynamic,
  SubViewDot,
  SubViewDotDynamic,
  type NavSection,
} from "./nav-pulse";
import type { TelemetryNavEntryDTO } from "@excellent/ui/api/contracts/pulse-telemetry";

/**
 * The primary navigation — Verification first, then the broader workspace modes
 * (Work · Customers · Product · Operations) as a VERTICAL list pinned to the left edge, centered
 * against the full viewport height (rather than the post-logo box, which biased
 * it below center). (Hiring is a Domain of Operations, not a top-level mode —
 * 2026-06-17, reverting LOCKED #2.)
 *
 * Under the ACTIVE mode it now reveals that mode's views (e.g. Customers →
 * Pulse · List · Board · Forecast · Queue) as a nested sub-nav. The views are
 * lifted out of the page (the old top-of-page tab strip) by the mounted page via
 * `ModeSubNavRegistrar`; the rail reads them with `useModeSubNav`. Switching
 * modes plays a single calm cross-fade — the outgoing mode's views collapse and
 * fade out AT THE SAME TIME as the incoming mode's expand and fade in (a block
 * accordion, no per-item slide), so the vertically-centered list barely shifts.
 * They read as a subtle, low-opacity layer beneath the mode label — quieter than
 * the modes themselves so the mode stays the primary affordance.
 *
 * Deliberately minimal: the rail is pure navigation chrome, NOT a surface — no
 * background, no border, no divider. Affordance lives entirely in the per-item
 * states: muted at rest, foreground on hover (text only, no background), and
 * foreground + medium weight when active.
 *
 * OUT OF FLOW: absolutely pinned to the left edge so it takes no layout width —
 * the app content stays centered in the full viewport, the rail just "lives
 * there" in the left margin. It is click-transparent except on the links
 * themselves (so the empty rail area never eats clicks meant for content).
 *
 * Hidden < md — narrow widths navigate from the bottom nav. The modes nav is
 * unconditional (the NEXT_PUBLIC_MODES_NAV flag is retired).
 */
export function ModeRail({
  pulseEnabled = false,
  pulseDynamicEnabled = false,
}: {
  /** Render the per-mode Pulse sparkline + sub-view dots (workspace setting,
   *  computed server-side in the root layout). Off ⇒ the rail fetches nothing. */
  pulseEnabled?: boolean;
  /** Data plane for BOTH the mode-row sparkline AND the expanded sub-nav dots:
   *  the learned-baseline telemetry (docs/pulse/DYNAMIC-PULSE-INTEGRATION-PRD.md
   *  §4 P6) instead of the classic wall. Orthogonal to `pulseEnabled` (whether
   *  the rail decorates at all) — a no-op unless `pulseEnabled` is also true.
   *  When on, the classic per-mode wall is not fetched at all (one nav-telemetry
   *  call feeds every decoration). */
  pulseDynamicEnabled?: boolean;
}) {
  const pathname = usePathname();
  const search = useSearch();
  // Project scope from the LIVE pathname, NOT a server prop. The root layout
  // resolves the project once and Next.js preserves it across soft navigations,
  // so a `projectSlug` prop would freeze on the first load — the rail's mode
  // links (`modePath`) + active matching (`isModeActive`) would then point at /
  // light the previous project after a soft project switch. `usePathname()` is
  // reactive, so deriving here keeps both correct. (Same fix as the top-nav
  // breadcrumb; shares `projectSlugFromPath` with the server resolver.)
  const projectSlug = projectSlugFromPath(pathname);
  // An orphan (mode-agnostic) route like /o/<uid> anchors to its origin mode via
  // ?from — so the rail keeps that mode lit even though the path carries no mode.
  const from = search?.get(NAV_FROM_PARAM) ?? null;
  // The live registration carries WHICH mode it belongs to (`subNavMode`), so we
  // can ignore a stale cross-mode signal during the navigation window.
  const { items: subNavItems, mode: subNavMode } = useModeSubNav();
  const activeSlug = useMemo(
    () => MODES.find((m) => isModeActive(pathname, m.slug, projectSlug, from))?.slug ?? null,
    [pathname, projectSlug, from],
  );

  // Per-mode snapshot of sub-nav items, keyed by slug. The mounted page registers
  // ONE set of views (the active mode's) through the global signal; we remember
  // them per mode so a mode that is animating CLOSED keeps rendering ITS OWN
  // views while the incoming mode opens — the signal has already swapped to the
  // new mode by then. This is what lets the outgoing and incoming sub-navs
  // collapse and expand at the SAME TIME, instead of the old snap-shut + restagger.
  const [itemsBySlug, setItemsBySlug] = useState<Record<string, ModeSubNavItem[]>>({});
  useEffect(() => {
    // Only cache a registration that belongs to the CURRENTLY-active mode. On a
    // mode switch the pathname (→ activeSlug) flips a frame before the incoming
    // page registers its own views, so the OUTGOING page's items are briefly live
    // under their own `subNavMode`; attributing those to the new activeSlug is the
    // cross-mode leak (Work's views appearing under Customers). Because we now
    // store the active mode's items even when EMPTY, a cold-start mode (which
    // registers no views) overwrites any stale entry and shows no sub-nav.
    if (!subNavMode || subNavMode !== activeSlug) return;
    setItemsBySlug((prev) =>
      prev[subNavMode] === subNavItems
        ? prev
        : { ...prev, [subNavMode]: subNavItems ?? [] },
    );
  }, [subNavMode, activeSlug, subNavItems]);

  // The nav telemetry op is fetched ONCE here (not per row) — every ModeRow
  // below reads its own mode's entry out of this map. A no-op map (nothing
  // fetched) when Pulse or dynamic is off.
  const navTelemetry = useNavTelemetry(pulseEnabled && pulseDynamicEnabled);

  return (
    <nav
      aria-label="Primary"
      // Centered against the FULL viewport height (inset-y-0), not the
      // post-logo box — anchoring the top at --nav-clearance biased the
      // midpoint downward. It sits well clear of the logo regardless.
      className="pointer-events-none absolute inset-y-0 left-0 z-30 hidden w-36 flex-col justify-center pl-3 md:flex"
    >
      <ul className="pointer-events-auto flex flex-col gap-0.5">
        {MODES.map((m) => {
          if (DEPRECATED_OLD_MODE_SLUGS.has(m.slug)) {
            return renderDeprecatedModeGroup(m);
          }
          return (
            <Fragment key={m.slug}>
              {m.slug === "agents" ? <ModeGroupLabel label="Verifier product" /> : null}
              {/* Plain breathing room (deliberately NO rule/hairline) separates the
                  org modes (workspace) from the owner-space group below. */}
              {m.slug === "personal" ? (
                <li aria-hidden className="pointer-events-none h-5" />
              ) : null}
              <ModeRow
                mode={m}
                active={m.slug === activeSlug}
                items={itemsBySlug[m.slug]}
                projectSlug={projectSlug}
                pulseEnabled={pulseEnabled}
                dynamicEnabled={pulseDynamicEnabled}
                navEntry={navTelemetry.get(m.slug)}
                icon={m.slug === "personal" ? "bookmark" : undefined}
              />
            </Fragment>
          );
        })}
      </ul>
    </nav>
  );
}

/** HG10: Work and Customers live in ~/Sites/excellent-classic, not this rail. */
const DEPRECATED_OLD_MODE_SLUGS = new Set(["work", "customers"]);

function renderDeprecatedModeGroup(m: (typeof MODES)[number]) {
  return m.slug === "work" ? (
    <ModeGroupLabel key="workspace-group" label="Workspace" className="mt-4" />
  ) : null;
}

function ModeGroupLabel({ label, className }: { label: string; className?: string }) {
  return (
    <li
      aria-hidden
      className={cn(
        "px-2.5 pb-1 pt-1.5 type-subtext font-medium uppercase tracking-wide text-faint",
        className,
      )}
    >
      {label}
    </li>
  );
}

/**
 * The owner-group rows' trailing glyph (Personal) — sits in the same
 * right-anchored slot the mode rows use for their Pulse sparkline, quieter
 * than the label (the sub-nav icons' opacity ramp).
 */
function RowGlyph({ name, active }: { name: string; active: boolean }) {
  return (
    <Icon
      name={name}
      size="sm"
      className={cn(
        "ml-auto transition-opacity",
        active ? "opacity-90" : "opacity-50 group-hover:opacity-80",
      )}
    />
  );
}

/**
 * One mode row: the mode link + its trailing Pulse micro-sparkline, plus the
 * nested (animated) sub-nav when items exist. Extracted from the rail's map so a
 * per-mode `useModeWall` hook can read THIS mode's health (one stable hook per
 * row — the rail's 5 reads dedupe against the warm preloaded cache). When Pulse
 * is off — or the DYNAMIC plane is on (then the one nav-telemetry call feeds both
 * the sparkline and the sub-view dots) — `useModeWall(null)` fetches nothing and
 * the row's classic-wall reads render exactly as before.
 */
function ModeRow({
  mode: m,
  active,
  items,
  projectSlug,
  pulseEnabled,
  dynamicEnabled,
  navEntry,
  icon,
}: {
  mode: (typeof MODES)[number];
  active: boolean;
  items: ModeSubNavItem[] | undefined;
  projectSlug?: string | null;
  pulseEnabled: boolean;
  /** Data plane for BOTH the sparkline and the sub-nav dots: telemetry (this
   *  mode's nav entry) instead of the classic wall. When true the classic wall
   *  is not fetched — the one nav-telemetry call carries both decorations. */
  dynamicEnabled: boolean;
  navEntry: TelemetryNavEntryDTO | undefined;
  /** Trailing glyph instead of the Pulse sparkline (the owner-space rows —
   *  Personal carries no business KPIs, so bars never made sense there). */
  icon?: string;
}) {
  // The classic per-mode wall feeds BOTH the sparkline and the sub-view dots —
  // but only when the dynamic plane is OFF. When it's on, the single
  // nav-telemetry call (`navEntry`) carries both, so we skip this fetch entirely
  // (killing the 6 per-mode wall requests). `dynamicEnabled` is a stable
  // server-computed prop, so the hook argument stays stable across renders.
  const { overall, sections } = useModeWall(pulseEnabled && !dynamicEnabled ? m.slug : null);
  // UI-UX-PATTERNS-PRD Issue 1b (Matt, 2026-07-15): the MODE's name is its canonical
  // identity — "Customers" — consistent across nav = pulse band = breadcrumb = title.
  // The C15 Lexicon grounds the OBJECT nouns inside a mode (the portfolio's parties
  // read "Accounts"/"Clients"/"Patients"), NOT the mode's own name in the primary
  // nav — grounding only the rail read "Accounts" while the title read "Customers",
  // a split Matt called "too visually off". So the rail uses the canonical label.
  const label = m.label;
  // Defer the Pulse decorations to AFTER hydration. The rail is always-SSR'd
  // global chrome, but its wall data comes only from the client SWR cache (the
  // server cache provider is empty, the client's is pre-warmed from localStorage).
  // Rendering directly from `sections` would make the first client render disagree
  // with the server-empty HTML — a React hydration mismatch on every warm reload.
  // Gating on `mounted` makes the first client render match the server (no bars/
  // dots), then fills them in a frame later — unobtrusive: inactive bars are neutral
  // gray, the dots only show watch/risk, and the one active row fills in its health
  // color (a single calm fill on the row you're already looking at, never a mismatch).
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const wallSections = mounted ? sections : [];
  const wallOverall = mounted ? overall : null;
  // Same post-hydration gate as the classic wall above — the nav telemetry map
  // is ALSO client-SWR-only, so the first client render must match the
  // server's (no sparkline) to avoid the same hydration-mismatch class.
  const showDynamic = mounted && dynamicEnabled;
  return (
    <li>
      <Link
        href={modePath(m.slug, { projectSlug })}
        aria-current={active ? "page" : undefined}
        className={cn(
          "group flex w-full items-center rounded-md px-2.5 py-1.5 type-body tracking-tight outline-none transition-colors",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
          // Active = full-strength foreground + medium (the strongest the
          // Regular/Medium ramp allows); inactive at the `faint` tier —
          // dimmer than `subtle` so the active mode unmistakably reads as
          // "you are here" without adding a background to this
          // deliberately-flat rail. That intent used to be spelled
          // `text-muted-foreground/70`, which rendered 2.34:1 in light and
          // desynced from the sub-view rows below (they dimmed off
          // --text-strong instead). `faint` is the same intent as a tier.
          active
            ? "font-medium text-foreground"
            : "text-faint hover:text-foreground",
        )}
      >
        <span className="truncate">{label}</span>
        {icon ? (
          <RowGlyph name={icon} active={active} />
        ) : showDynamic ? (
          <NavSparklineDynamic entry={navEntry} label={label} />
        ) : (
          <NavSparkline sections={wallSections} label={label} overall={wallOverall} />
        )}
      </Link>
      {items && items.length > 0 ? (
        // Stays mounted once a mode has been visited so it can animate CLOSED (not
        // just vanish). `open` drives the expand/collapse, so the outgoing mode
        // collapses while the incoming one expands. The per-row status dots read
        // the DYNAMIC nav entry when `dynamic` is on, else `sections` (the classic
        // per-mode wall's section health).
        <ModeSubNav
          items={items}
          open={active}
          sections={wallSections}
          dynamic={showDynamic}
          navEntry={navEntry}
        />
      ) : null}
    </li>
  );
}

/** The active marker is a fixed icon-height (14px) bar; the guide line is sized
 *  to the range that bar's centre can travel, so the end rows sit flush with it. */
const MARKER_H = 14;
const MARKER_HALF = MARKER_H / 2;

/**
 * The active mode's views, nested + animated. The whole block is ONE accordion:
 * height (grid-rows 0fr↔1fr) and opacity transition together, driven by the
 * `open` prop. Because the outgoing mode's block is kept mounted and collapses
 * while the incoming one expands, the two animate simultaneously and the
 * vertically-centered mode list stays put. Deliberately NO per-item stagger or
 * horizontal slide — that was the "everything flying" motion; a single calm
 * block fade reads as elegant and is robust to views arriving a frame late.
 * Subtle opacity — quieter than the mode labels.
 */
function ModeSubNav({
  items,
  open,
  sections,
  dynamic,
  navEntry,
}: {
  items: ModeSubNavItem[];
  open: boolean;
  /** This mode's classic-wall section healths — feeds each sub-view's trailing
   *  status dot when `dynamic` is off. */
  sections: NavSection[];
  /** Read the per-sub-view dots from the DYNAMIC telemetry plane (`navEntry`)
   *  instead of the classic wall (`sections`). */
  dynamic: boolean;
  /** This mode's nav telemetry entry — the source for the dynamic dots. */
  navEntry: TelemetryNavEntryDTO | undefined;
}) {
  const pathname = usePathname();
  const searchParams = useSearch();
  const navParams = useMemo(() => {
    const p = new URLSearchParams(searchParams?.toString() ?? "");
    // A Pulse drill into a band (`?g=<band>&t=<touch>`, e.g. `/work?g=queue`) IS
    // being inside that section — the band slug equals the section/lane area
    // slug. The lane itself lives at `?area=<band>`, so mirror `g` onto `area`
    // for active matching and the section's sub-view lights up ("you are here").
    if (!p.get("area") && p.get("g")) p.set("area", p.get("g")!);
    return p;
  }, [searchParams]);
  const activeTarget = useMemo(
    () => pickActiveTarget(pathname, navParams, items.map((i) => i.activeHref ?? i.href)),
    [pathname, navParams, items],
  );

  // The active view is marked by a single SLIDING bar, not a per-item border —
  // a fixed icon-height (14px) accent overlaid on the guide line. We measure the
  // active row's vertical centre and drive the bar's `top` from state, so
  // switching views ANIMATES the bar from the old row to the new one. The first
  // measurement (initial paint, or a mode re-opened with its panel still mounted)
  // renders the bar straight at its spot with no prior value to transition from —
  // so a section switch lands instantly and only an in-section change slides.
  const listRef = useRef<HTMLUListElement>(null);
  const [markerTop, setMarkerTop] = useState<number | null>(null);
  const [line, setLine] = useState<{ top: number; height: number } | null>(null);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const measure = () => {
      const listTop = list.getBoundingClientRect().top;
      const centreOf = (el: HTMLElement) => {
        const r = el.getBoundingClientRect();
        return r.top - listTop + r.height / 2;
      };
      const rows = Array.from(list.querySelectorAll<HTMLElement>(":scope > li"));
      if (rows.length === 0) {
        setLine(null);
        setMarkerTop(null);
        return;
      }
      // The guide line spans ONLY the range the marker can travel: from the top
      // edge of the marker centred on the first row to its bottom edge on the
      // last. So selecting the top or bottom view leaves the line flush with the
      // marker — no overhang past either end.
      const first = centreOf(rows[0]!);
      const last = centreOf(rows[rows.length - 1]!);
      setLine({ top: first - MARKER_HALF, height: last - first + MARKER_H });
      const activeEl = list.querySelector<HTMLElement>('[data-active="true"]');
      setMarkerTop(activeEl ? centreOf(activeEl) : null);
    };
    measure();
    // Re-measure when the rail's metrics shift — icons that mount a frame late,
    // fonts settling, or the accordion finishing its expand.
    const ro = new ResizeObserver(measure);
    ro.observe(list);
    return () => ro.disconnect();
  }, [activeTarget, items, open]);

  // The block is one accordion: grid-rows (0fr↔1fr) + opacity transition together,
  // driven by `open`. The outgoing mode's block stays mounted and collapses while
  // the incoming one expands, so they animate simultaneously and the centred mode
  // list barely shifts. `@starting-style` (the `starting:` utils) animates a panel
  // that mounts already-open (first visit) from collapsed, with zero JS deferral.
  return (
    <div
      className={cn(
        "grid transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:!transition-none",
        open
          ? "grid-rows-[1fr] opacity-100 starting:grid-rows-[0fr] starting:opacity-0"
          : "grid-rows-[0fr] opacity-0",
      )}
    >
      {/* `inert` when closed: a collapsed block stays mounted (so it can animate
          shut), but its links must not be tab-focusable or announced while
          invisible. */}
      <div className="overflow-hidden" inert={!open}>
        {/* A faint 1px hairline runs down the left of the indented views, but
            ONLY across the range the marker can travel (measured top/height) — so
            the first/last rows sit flush with no overhang. The active view is
            marked by a single 2px bar sized to the icon (14px), centred on the
            row and slid into place; it shares the line's left edge so its extra
            pixel of width is biased RIGHT, toward the label. */}
        <ul
          ref={listRef}
          className="relative mt-0.5 ml-3 flex flex-col gap-px pb-0.5"
        >
          {line ? (
            <span
              aria-hidden
              className="pointer-events-none absolute left-0 w-px bg-foreground/10 transition-[top,height] duration-300 ease-out motion-reduce:transition-none"
              style={{ top: line.top, height: line.height }}
            />
          ) : null}
          {markerTop !== null ? (
            <span
              aria-hidden
              className="pointer-events-none absolute left-0 h-3.5 w-0.5 -translate-y-1/2 rounded-full bg-foreground transition-[top] duration-300 ease-out motion-reduce:transition-none"
              style={{ top: markerTop }}
            />
          ) : null}
          {items.map((item) => {
            const target = item.activeHref ?? item.href;
            const active = target === activeTarget;
            return (
              <li key={item.href}>
                <Link
                  href={item.href}
                  data-active={active ? "true" : undefined}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "flex items-center gap-2 py-1 pl-3 pr-2.5 type-subtext tracking-tight outline-none transition-colors",
                    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
                    active
                      ? "font-medium text-foreground/90"
                      : "text-faint hover:text-foreground",
                  )}
                >
                  {/* No icons in the nav — EVER (Matt, 2026-07-23). The label +
                      the trailing health dot (an indicator, allowed) carry it. */}
                  <span className="truncate">{item.label}</span>
                  {/* Trailing health dot (problem tones only — healthy stays
                      silent). An xs dot inside the existing flex row, so it
                      never changes the row height the sliding-marker measures.
                      `area` is the exact pulse join key (route-owning rows whose
                      tail ≠ pulse area, e.g. Workflows→fleet); falls back to the
                      href tail when absent. The DYNAMIC dot reads the telemetry
                      plane's per-area finding fold; the classic dot reads the
                      per-mode wall's section health. */}
                  {dynamic ? (
                    <SubViewDotDynamic entry={navEntry} href={item.href} area={item.area} />
                  ) : (
                    <SubViewDot sections={sections} href={item.href} area={item.area} />
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
